import { useState } from 'react';
import {fetchStocks, addStock} from '../services/stockService';
import { flattenProductData, getActiveFilters } from '../utils/helper';
import API from "../../../../network/dashboardAPI";

export const useAddStock = (filters, page, limit, orderField, orderType, setStocks, setTotalPages, setError, getWarehouseLocations) => {
    const [showAddRow, setShowAddRow] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [newStock, setNewStock] = useState({
        supplier: null,
        supplier_product_name: '',
        supplier_product_number: '',
        warehouse: null,
        location_name: '',
        available_quantity: ''
    });
    const [supplierProductNumberMap, setSupplierProductNumberMap] = useState([]);
    const [supplierProductMap, setSupplierProductMap] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [warehouseLocations, setWarehouseLocations] = useState([]);

    // Handle new stock input changes
    const handleNewStockChange = (field, value) => {
        if (field === 'supplier') {
            setNewStock(prev => ({
                ...prev,
                warehouse: null,
                supplier_product_name: '',
                supplier_product_number: ''
            }));
            if (value) {
                const postData = { "supplier_id": value.store_user_id };
                API.getAdminSupplierProductBySupplierId(postData)
                    .then((response) => response.json())
                    .then((response) => {
                        const supplierProducts = (response.data || []).map(item => ({
                            value: item.supplier_product_number,
                            label: item.supplier_product_name,
                        }));
                        const productNumbers = (response.data || []).map(item => ({
                            value: item.supplier_product_number,
                            label: item.supplier_product_number,
                        }));
                        setSupplierProductNumberMap(productNumbers);
                        setSupplierProductMap(supplierProducts);
                    });
                API.getWarehouse({
                    filters: {
                        supplier: {
                            store_user_id: value.store_user_id
                        }
                    }
                })
                    .then((response) => response.json())
                    .then((response) => {
                        if (response.success) {
                            setWarehouses(response.list);
                        }
                    });
                setNewStock(prev => ({
                    ...prev,
                    [field]: value
                }));
            }
        } else if (field === 'warehouse' && value) {
            API.getWarehouseLocationsByWarehouseID({
                warehouse_id: value.id
            })
                .then((response) => response.json())
                .then((response) => {
                    if (response.success) {
                        const warehouseLocationRecords = (response.list || []).map(item => ({
                            value: item.name,
                            label: item.name,
                        }));
                        setWarehouseLocations(warehouseLocationRecords);
                    }
                });
            setNewStock(prev => ({
                ...prev,
                [field]: value
            }));
        } else if (field === 'supplier_product_number') {
            const selectedProduct = supplierProductMap.find(product => product.value === value?.value);
            setNewStock(prev => ({
                ...prev,
                supplier_product_number: value,
                supplier_product_name: selectedProduct ? selectedProduct.label : prev.supplier_product_name
            }));
            return;
        } else if (field === 'location_name' && value) {
            setNewStock(prev => ({
                ...prev,
                [field]: value.value,
            }));
        } else {
            setNewStock(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    // Handle add stock submission
    const handleAddStock = async () => {
        try {
            setIsLoading(true);
            const stockData = {
                supplier_id: newStock.supplier?.store_users[0].id,
                supplier_product_name: newStock.supplier_product_name,
                supplier_product_number: newStock.supplier_product_number?.value || newStock.supplier_product_number,
                warehouse_id: newStock.warehouse?.id,
                location_name: newStock.location_name,
                available_quantity: parseInt(newStock.available_quantity)
            };
            await addStock(stockData);
            
            // Refresh warehouse locations cache for the warehouse that was just updated
            if (newStock.warehouse?.id && getWarehouseLocations) {
                await getWarehouseLocations(newStock.warehouse.id, true); // Force refresh
            }
            
            setNewStock({
                supplier: null,
                supplier_product_name: '',
                supplier_product_number: '',
                warehouse: null,
                location_name: '',
                available_quantity: ''
            });
            setShowAddRow(false);
            // Refresh stocks
            const filtersToSend = getActiveFilters(filters);
            const data = await fetchStocks(page, limit, orderField, orderType, filtersToSend);
            setTotalPages(data.total_pages);
            if (data && data.list) {
                const flattenData = flattenProductData(data.list, orderField, orderType);
                setStocks(flattenData);
            }
        } catch (err) {
            console.error('Error adding stock:', err);
            setError('Failed to add stock. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return {
        showAddRow,
        setShowAddRow,
        isLoading,
        newStock,
        supplierProductNumberMap,
        supplierProductMap,
        warehouses,
        warehouseLocations,
        handleNewStockChange,
        handleAddStock,
    };
};
